import { useState, useEffect } from "react";
import { getSession, checkIsAdmin, onAuthStateChange, getProfile, getCurrentUser, signOut } from "@/lib/api";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import { changeLanguage } from "@/lib/i18n/config";

interface AuthState {
  user: SupabaseUser | null;
  isAdmin: boolean;
  loading: boolean;
}

// Module-level cache to persist auth state across component remounts
let cachedUser: SupabaseUser | null = null;
let cachedIsAdmin = false;
let hasInitialized = false;
let initPromise: Promise<void> | null = null;

// Helper to clear stale session
async function clearStaleSession() {
  cachedUser = null;
  cachedIsAdmin = false;
  try {
    await signOut();
  } catch (_e) {
    // Ignore signout errors
  }
}

export function useAuthState(): AuthState {
  const [user, setUser] = useState<SupabaseUser | null>(cachedUser);
  const [isAdmin, setIsAdmin] = useState(cachedIsAdmin);
  const [loading, setLoading] = useState(!hasInitialized);

  useEffect(() => {
    const init = async () => {
      // Skip if already initialized
      if (hasInitialized) {
        setUser(cachedUser);
        setIsAdmin(cachedIsAdmin);
        setLoading(false);
        return;
      }

      // Reuse existing init promise to prevent race conditions

      // Reuse existing init promise to prevent race conditions
      if (!initPromise) {
        initPromise = (async () => {
          try {
            const sessionResult = await getSession();
            if (sessionResult.success && sessionResult.data?.user) {
              // Verify the user actually exists in auth.users (not a stale JWT)
              const userResult = await getCurrentUser();
              if (!userResult.success) {
                // User no longer exists - clear the stale session
                console.warn("Stale session detected, clearing...");
                await clearStaleSession();
              } else {
                cachedUser = userResult.data;
                const adminResult = await checkIsAdmin();
                cachedIsAdmin = adminResult.success && adminResult.data;
                
                // Apply user's preferred language on initial load
                const profileResult = await getProfile(cachedUser.id);
                if (profileResult.success && profileResult.data?.preferred_language) {
                  await changeLanguage(profileResult.data.preferred_language as any);
                }
              }
            }
          } catch (error) {
            console.error("Failed to get session:", error);
            await clearStaleSession();
          } finally {
            hasInitialized = true;
          }
        })();
      }

      await initPromise;
      setUser(cachedUser);
      setIsAdmin(cachedIsAdmin);
      setLoading(false);
    };

    init();

    const subscription = onAuthStateChange(async (session) => {
      // CRITICAL: Reset cache on ANY auth state change to prevent stale admin status
      cachedIsAdmin = false;
      hasInitialized = false;
      initPromise = null;
      
      if (session?.user) {
        // Verify the user exists before caching
        const userResult = await getCurrentUser();
        if (!userResult.success) {
          await clearStaleSession();
          setUser(null);
          setIsAdmin(false);
          return;
        }
        cachedUser = session.user;
        setUser(cachedUser);
        
        // Check admin status with retry logic
        let adminResult = await checkIsAdmin();
        cachedIsAdmin = adminResult.success && adminResult.data;
        
        // If admin check fails, retry once after a short delay (race condition mitigation)
        if (!cachedIsAdmin) {
          await new Promise(r => setTimeout(r, 150));
          adminResult = await checkIsAdmin();
          cachedIsAdmin = adminResult.success && adminResult.data;
        }
        
        setIsAdmin(cachedIsAdmin);
        hasInitialized = true;
        
        // Apply user's preferred language on login
        const profileResult = await getProfile(session.user.id);
        if (profileResult.success && profileResult.data?.preferred_language) {
          await changeLanguage(profileResult.data.preferred_language as any);
        }
      } else {
        cachedUser = null;
        cachedIsAdmin = false;
        hasInitialized = true;
        setUser(null);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return { user, isAdmin, loading };
}
